/* ============================================================
   SAM-1073 — pure model of the New service flow (Kind · Details · Look · Booking & price):
   what each step needs before Continue, the Free/Paid switch on the form, and the "Guests can
   start at" line. The screens live in NewServiceWizard.tsx; nothing here touches React.
   ============================================================ */
import { EMPTY_OPTION, type FormData } from '../../../hooks/useServiceEditor';
import { isEventSubtype } from '../../../utils/eventSize';
import { usesUnits } from './serviceCreateTaxonomy';
import { withCurrency, pricingCurrency } from '../editorV2';

export type WizardStep = 'kind' | 'details' | 'look' | 'booking';

export const WIZARD_STEPS: Array<{ key: WizardStep; label: string; title: string }> = [
  { key: 'kind', label: 'Kind', title: 'what kind of service?' },
  { key: 'details', label: 'Details', title: 'details' },
  { key: 'look', label: 'Look', title: 'look in the app' },
  { key: 'booking', label: 'Booking & price', title: 'booking and price' },
];

export const stepIndex = (step: WizardStep): number => WIZARD_STEPS.findIndex((s) => s.key === step);

/** A whole-day setup has no booking length and no start times (the guest picks a date). */
export const hasSlots = (form: Pick<FormData, 'booking_type'>): boolean => form.booking_type !== 'day';

/** Start times repeat within the booking length only for fixed-slot services (settings.intervals.bookable_interval). */
export const hasStartTimes = (form: Pick<FormData, 'booking_type'>): boolean => form.booking_type === 'fixed';

/** Events are priced by size bands; those are set in the full editor after creating. */
export const pricedByEventSize = (form: Pick<FormData, 'price_unit' | 'subtype'>): boolean =>
  form.price_unit === 'event' || isEventSubtype(form.subtype);

/** Free = no booking option, the editor's own meaning of "implicitly free". */
export const isFreeForm = (form: Pick<FormData, 'booking_options'>): boolean => form.booking_options.length === 0;

/** Tables start free (they are free or deposit); everything else is sold from the start. */
export const startsFree = (subtype: string): boolean => subtype === 'tables';

/** Free ON clears prices; OFF seeds the one guest line a priced service needs (unit-priced for units). */
export function setFree(form: FormData, free: boolean): FormData {
  if (free) {
    // Free clears every price the wizard can have set, and the unit-priced model with them: a unit-priced
    // service without a unit price is not valid (QA 21 oct: Free ON > OFF > ON left Create disabled).
    const unit = form.price_unit === 'unit';
    return {
      ...form, booking_options: [], unit_price: '',
      ...(unit ? { price_unit: 'person' as const, unit_included_pax: '', unit_max_pax: '' } : {}),
    };
  }
  if (form.booking_options.length > 0) return form;
  const currency = pricingCurrency(form);
  const next: FormData = {
    ...form,
    booking_options: [{ ...EMPTY_OPTION, id: 'guest', label_en: 'Guest', label_es: 'Huésped', price: '', min_amount: '1' }],
  };
  if (!pricedByEventSize(form) && usesUnits(form.subtype) && form.price_unit === 'person') {
    // A unit-priced service charges the unit; its guest line carries only an extra-guest price (0 = none).
    next.price_unit = 'unit';
    next.booking_options = next.booking_options.map((o) => ({ ...o, extra_guest_price: '0' }));
  }
  return withCurrency(next, currency);
}

/** The flat price the wizard edits: the unit price for a unit-priced service, else the guest line's price. */
export const priceOf = (form: FormData): string =>
  form.price_unit === 'unit' ? form.unit_price : form.booking_options[0]?.price ?? '';

export function setPrice(form: FormData, price: string): FormData {
  if (form.price_unit === 'unit') return { ...form, unit_price: price };
  return { ...form, booking_options: form.booking_options.map((o, i) => (i === 0 ? { ...o, price } : o)) };
}

export function setCurrency(form: FormData, currency: string): FormData {
  return withCurrency(form, currency);
}

const MINUTES_IN_DAY = 24 * 60;

/** Start-time step in minutes: its own value, else the booking length, else 60. */
export function startStepMinutes(form: Pick<FormData, 'bookable_interval' | 'interval_duration'>): number {
  const own = Number(form.bookable_interval);
  if (Number.isFinite(own) && own > 0) return own;
  const len = Number(form.interval_duration);
  return Number.isFinite(len) && len > 0 ? len : 60;
}

/** "9:00 AM" */
export function formatClock(totalMinutes: number): string {
  const m = ((Math.round(totalMinutes) % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
  const h24 = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, '0');
  const suffix = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${mm} ${suffix}`;
}

/** The first `count` start times of the first open day (falls back to 9:00 AM), within its hours. */
export function sampleStartTimes(form: Pick<FormData, 'operating_hours' | 'bookable_interval' | 'interval_duration'>, count: number): string[] {
  const day = Object.values(form.operating_hours).find((d) => d.enabled && d.open) ;
  const [oh, om] = (day?.open || '09:00').split(':').map(Number);
  const [ch, cm] = (day?.close || '18:00').split(':').map(Number);
  const open = (oh || 0) * 60 + (om || 0);
  const close = (ch || 0) * 60 + (cm || 0);
  const step = startStepMinutes(form);
  const length = Number(form.interval_duration) > 0 ? Number(form.interval_duration) : step;
  const out: string[] = [];
  for (let t = open; t + length <= close && out.length < count; t += step) out.push(formatClock(t));
  return out.length ? out : [formatClock(open)];
}

/** What Continue needs on each step; `null` when it can go on. */
export function stepBlocker(step: WizardStep, form: FormData, free: boolean): string | null {
  if (step === 'kind') {
    if (!form.business_id) return 'Choose the business first.';
    return form.subtype ? null : 'Pick a kind.';
  }
  if (step === 'details') return form.name_en.trim() ? null : 'Add a name in English.';
  if (step === 'booking') {
    if (hasSlots(form) && !(Number(form.interval_duration) > 0)) return 'Say how long a booking is.';
    if (!free && !pricedByEventSize(form)) {
      const price = priceOf(form).trim();
      if (price === '' || Number.isNaN(Number(price)) || Number(price) < 0) return 'Add a price, or switch on Free to book.';
    }
  }
  return null;
}
